import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Api, QualityInspectResponse, RepairOneResponse } from "../api/client";
import { format, addDays } from "date-fns";
import {
  Sparkles, AlertTriangle, CheckCircle2, XCircle, RefreshCw, Wrench,
  Hourglass, Loader2, Square, Layers,
} from "lucide-react";

const SIGN_NAMES: Record<string, string> = {
  aries: "Овен", taurus: "Телец", gemini: "Близнецы", cancer: "Рак",
  leo: "Лев", virgo: "Дева", libra: "Весы", scorpio: "Скорпион",
  sagittarius: "Стрелец", capricorn: "Козерог", aquarius: "Водолей", pisces: "Рыбы",
};

const ISSUE_LABELS: Record<string, string> = {
  missing_collective_context: "Нет collective_context",
  short_forecast_variants: "Короткие forecast variants",
  short_general_horoscope: "Короткий general_horoscope",
  parser_placeholder_archetype: "Заглушка archetype",
  parser_placeholder_tone_profile: "Заглушка tone_profile",
};

type RowState =
  | { kind: "idle" }
  | { kind: "running"; startedAt: number }
  | { kind: "ok"; durationMs: number; costRub: number }
  | { kind: "fail"; durationMs: number; error: string };

interface RunStats {
  done: number;
  ok: number;
  fail: number;
  totalCost: number;
  startedAt: number | null;
}

const EMPTY_STATS: RunStats = { done: 0, ok: 0, fail: 0, totalCost: 0, startedAt: null };

export function DataQuality() {
  const queryClient = useQueryClient();
  const today = format(new Date(), "yyyy-MM-dd");
  const inThreeMonths = format(addDays(new Date(), 90), "yyyy-MM-dd");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(inThreeMonths);
  const [maxBatch, setMaxBatch] = useState(100);

  // Concurrency — потоки параллельных AI-вызовов. Дефолт берём из generation-settings
  // (тот же BackfillConcurrency, что и стандартный backfill).
  // Верхний клиентский предел — 15 (синхронизирован с server-side SemaphoreSlim
  // в /api/repair/sign-day-core/repair-one); выше worker всё равно начнёт парковать
  // запросы в очереди семафора, и UI просто будет дольше ждать без выгоды.
  const [concurrency, setConcurrency] = useState(5);
  const [concurrencyTouched, setConcurrencyTouched] = useState(false);
  const CLIENT_MAX_CONCURRENCY = 15;

  // Per-row state. Map id → RowState.
  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});
  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState<RunStats>(EMPTY_STATS);
  const abortRef = useRef<AbortController | null>(null);

  // Ticker — пере-рендер каждые 500ms чтобы elapsed-seconds для running-строк обновлялись.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => forceTick((x) => x + 1), 500);
    return () => clearInterval(id);
  }, [running]);

  // Подгружаем дефолтный concurrency из тех же настроек, которыми
  // пользуется фоновый BackfillJob — чтобы UI repair не упирался в
  // другие лимиты, чем стандартный AI-сервер.
  const settings = useQuery({
    queryKey: ["generation-settings"],
    queryFn: Api.generationSettings,
  });
  useEffect(() => {
    if (!concurrencyTouched && settings.data?.backfillConcurrency) {
      setConcurrency(Math.min(CLIENT_MAX_CONCURRENCY, settings.data.backfillConcurrency));
    }
  }, [settings.data?.backfillConcurrency, concurrencyTouched]);

  const inspect = useQuery<QualityInspectResponse>({
    queryKey: ["quality-inspect", from, to],
    queryFn: () => Api.qualityInspect(from, to),
    // Во время repair дёргаем inspect раз в 15с, чтобы счётчик low-quality
    // и таблица проблемных строк уменьшались по мере фикса (видно прогресс).
    refetchInterval: running ? 15_000 : 30_000,
  });

  const data = inspect.data;
  const lowQ = data?.lowQualityCount ?? 0;
  const total = data?.inspectedRecords ?? 0;
  const ratio = total > 0 ? (lowQ / total) * 100 : 0;
  const allGood = total > 0 && lowQ === 0;

  // ----- Repair runner -----

  async function startRepair() {
    if (!data) return;
    const items = data.lowQualityItems.slice(0, maxBatch);
    if (items.length === 0) return;

    const ac = new AbortController();
    abortRef.current = ac;

    // Инициализируем все строки в очереди как "idle" (waiting)
    const initStates: Record<number, RowState> = {};
    items.forEach((it) => { initStates[it.id] = { kind: "idle" }; });
    setRowStates(initStates);
    setStats({ ...EMPTY_STATS, startedAt: Date.now() });
    setRunning(true);

    // Параллельно с ограничением `concurrency` — точно как BackfillOrchestrator
    // делает SemaphoreSlim(concurrency). На AI-сервере есть пул прокси/аккаунтов,
    // 5–15 параллельных запросов он спокойно обслуживает. Серийный режим был
    // безопасным, но превращал ремонт ~100 записей в полтора часа.
    const queue = [...items];
    const handleOne = async (it: { id: number }) => {
      // Mark running
      setRowStates((prev) => ({ ...prev, [it.id]: { kind: "running", startedAt: Date.now() } }));
      try {
        const r: RepairOneResponse = await Api.qualityRepairOne(it.id, ac.signal);
        if (r.success) {
          setRowStates((prev) => ({
            ...prev,
            [it.id]: { kind: "ok", durationMs: r.durationMs, costRub: r.costRub },
          }));
          setStats((s) => ({ ...s, done: s.done + 1, ok: s.ok + 1, totalCost: s.totalCost + r.costRub }));
        } else {
          setRowStates((prev) => ({
            ...prev,
            [it.id]: { kind: "fail", durationMs: r.durationMs, error: r.error || "unknown_failure" },
          }));
          setStats((s) => ({ ...s, done: s.done + 1, fail: s.fail + 1 }));
        }
      } catch (e: unknown) {
        const aborted = ac.signal.aborted;
        const msg = aborted
          ? "отменено"
          : (e instanceof Error ? e.message : String(e));
        setRowStates((prev) => ({
          ...prev,
          [it.id]: { kind: "fail", durationMs: 0, error: msg },
        }));
        setStats((s) => ({ ...s, done: s.done + 1, fail: s.fail + 1 }));
      }
    };

    // Worker-pool: запускаем `concurrency` worker'ов, каждый забирает следующую
    // запись из общей очереди. Когда очередь пустеет — worker завершается.
    // Таким образом всегда работает ровно `concurrency` параллельных AI-вызовов
    // (пока есть работа), и ни один лишний поток не висит.
    const poolSize = Math.max(1, Math.min(concurrency, items.length));
    const workers = Array.from({ length: poolSize }, async () => {
      while (!ac.signal.aborted) {
        const next = queue.shift();
        if (!next) return;
        await handleOne(next);
      }
    });
    await Promise.all(workers);

    setRunning(false);
    abortRef.current = null;
    queryClient.invalidateQueries({ queryKey: ["quality-inspect", from, to] });
  }

  function cancel() {
    abortRef.current?.abort();
  }

  function clearStates() {
    setRowStates({});
    setStats(EMPTY_STATS);
  }

  const visibleItems = data?.lowQualityItems.slice(0, maxBatch) ?? [];
  const elapsedSec = stats.startedAt ? Math.round((Date.now() - stats.startedAt) / 1000) : 0;
  // Throughput = stats.done / elapsedSec; ETA = remaining / throughput.
  // Это корректно учитывает параллелизм: если concurrency=5 и каждая задача 60с,
  // throughput ≈ 5/60 ≈ 0.083 task/s, ETA для 50 оставшихся ≈ 600с (10 минут).
  const remaining = Math.max(0, visibleItems.length - stats.done);
  const throughput = stats.done > 0 && elapsedSec > 0 ? stats.done / elapsedSec : 0;
  const etaSec = throughput > 0 ? Math.round(remaining / throughput) : 0;
  const progressPct = visibleItems.length > 0 ? (stats.done / visibleItems.length) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <Sparkles className="text-astro-violet" size={26} />
          Качество данных
        </h1>
        <button
          onClick={() => inspect.refetch()}
          disabled={inspect.isFetching || running}
          className="btn-secondary text-xs">
          <RefreshCw size={14} className={inspect.isFetching ? "animate-spin inline mr-1" : "inline mr-1"} />
          Обновить
        </button>
      </div>

      {/* Range picker */}
      <section className="card p-5">
        <h2 className="font-display text-base font-semibold mb-3">Диапазон проверки</h2>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-white/40 mb-1">От</label>
            <input
              type="date" value={from} disabled={running}
              onChange={(e) => setFrom(e.target.value)}
              className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">До</label>
            <input
              type="date" value={to} disabled={running}
              onChange={(e) => setTo(e.target.value)}
              className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Batch size</label>
            <input
              type="number" min={1} max={500} disabled={running}
              value={maxBatch} onChange={(e) => setMaxBatch(parseInt(e.target.value) || 100)}
              className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono w-24 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1 flex items-center gap-1">
              <Layers size={12} /> Потоки
              {settings.data && !concurrencyTouched && (
                <span className="text-white/30">(из настроек)</span>
              )}
            </label>
            <input
              type="number" min={1} max={CLIENT_MAX_CONCURRENCY} disabled={running}
              value={concurrency}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                setConcurrency(Number.isFinite(v) && v >= 1 ? Math.min(CLIENT_MAX_CONCURRENCY, v) : 1);
                setConcurrencyTouched(true);
              }}
              className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono w-20 disabled:opacity-50"
              title={`Параллельных AI-вызовов одновременно (1..${CLIENT_MAX_CONCURRENCY}). Дефолт = BackfillConcurrency со страницы Готовность. Server-side лимит = ${CLIENT_MAX_CONCURRENCY}.`}
            />
          </div>
        </div>
      </section>

      {/* Summary */}
      {inspect.isLoading && <p className="text-white/40 text-sm">Анализируем…</p>}
      {inspect.error && (
        <div className="card p-4 bg-rose-500/10 border-rose-500/30 text-rose-200 text-sm">
          Ошибка: {String((inspect.error as Error).message)}
        </div>
      )}
      {data && (
        <section className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-base font-semibold">Сводка</h2>
            {allGood ? (
              <span className="badge-ok flex items-center gap-1">
                <CheckCircle2 size={12} /> всё в порядке
              </span>
            ) : (
              <span className="badge-fail flex items-center gap-1">
                <AlertTriangle size={12} /> есть проблемы
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Всего записей" value={total} />
            <Stat label="Low quality" value={lowQ} accent={lowQ > 0 ? "warn" : "ok"} />
            <Stat
              label="Доля проблемных"
              value={`${ratio.toFixed(1)}%`}
              accent={ratio > 30 ? "warn" : ratio > 0 ? "neutral" : "ok"}
            />
            <Stat label="Знаков (max=12/день)" value={Math.ceil(lowQ / 12)} hint="~ дней под фикс" />
          </div>

          {Object.keys(data.issueCounts).length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <h3 className="text-xs uppercase text-white/40 mb-2">Типы проблем</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.issueCounts).map(([key, count]) => (
                  <span
                    key={key}
                    className="px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs"
                    title={key}
                  >
                    {ISSUE_LABELS[key] ?? key}: <b>{count}</b>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Repair action */}
      {data && lowQ > 0 && (
        <section className="card p-5">
          <h2 className="font-display text-base font-semibold mb-2 flex items-center gap-2">
            <Wrench size={18} /> Починить
          </h2>
          <p className="text-sm text-white/60 mb-3">
            Возьмёт первые <b>{Math.min(maxBatch, lowQ)}</b> записей. Каждая = AI-вызов
            Pipeline1 (~30-90 сек). Стоимость ~ <b>{(Math.min(maxBatch, lowQ) * 0.36).toFixed(2)} ₽</b>.
            Запросы идут параллельно (потоков: <b>{concurrency}</b>) — как стандартный
            backfill. Статус каждой записи виден в таблице ниже в реальном времени.
          </p>

          <div className="flex items-center gap-3 flex-wrap">
            {!running ? (
              <button
                onClick={startRepair}
                disabled={!data || visibleItems.length === 0}
                className="btn-primary"
              >
                <span className="flex items-center gap-2">
                  <Wrench size={14} /> Запустить починку (max={maxBatch})
                </span>
              </button>
            ) : (
              <button
                onClick={cancel}
                className="px-4 py-2 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-200 hover:bg-rose-500/30 transition flex items-center gap-2"
              >
                <Square size={14} /> Отменить
              </button>
            )}

            {(stats.done > 0 || running) && (
              <button
                onClick={clearStates}
                disabled={running}
                className="btn-secondary text-xs"
              >
                Сбросить статусы
              </button>
            )}
          </div>

          {/* Aggregate progress */}
          {(running || stats.done > 0) && (
            <div className="mt-4 pt-4 border-t border-white/5">
              {/* Progress bar */}
              <div className="h-2 rounded-full bg-black/30 overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-astro-violet to-astro-gold transition-all duration-200"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <Stat label="Готово" value={`${stats.done} / ${visibleItems.length}`} />
                <Stat label="✓ ok" value={stats.ok} accent="ok" />
                <Stat label="✗ ошибки" value={stats.fail} accent={stats.fail > 0 ? "warn" : "neutral"} />
                <Stat label="Стоимость" value={`${stats.totalCost.toFixed(2)} ₽`} />
                <Stat
                  label={running ? "Прошло / ETA" : "Заняло"}
                  value={running ? `${formatSec(elapsedSec)} / ~${formatSec(etaSec)}` : formatSec(elapsedSec)}
                />
              </div>
            </div>
          )}
        </section>
      )}

      {/* Items list with per-row status */}
      {data && data.lowQualityItems.length > 0 && (
        <section className="card p-5">
          <h2 className="font-display text-base font-semibold mb-3">
            Записи с проблемами (показано {Math.min(visibleItems.length, 100)} из {lowQ})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/40 text-xs uppercase">
                <tr>
                  <th className="text-left py-2 pr-3 w-32">Статус</th>
                  <th className="text-left py-2 pr-4">Дата</th>
                  <th className="text-left py-2 pr-4">Знак</th>
                  <th className="text-left py-2 pr-4">Проблемы</th>
                  <th className="text-right py-2">Версия</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.slice(0, 100).map((item) => {
                  const state = rowStates[item.id] ?? { kind: "idle" } as RowState;
                  return (
                    <tr key={item.id} className="border-t border-white/5">
                      <td className="py-2 pr-3"><RowStatusCell state={state} hasState={!!rowStates[item.id]} /></td>
                      <td className="py-2 pr-4 font-mono text-white/70">{item.forecastDate}</td>
                      <td className="py-2 pr-4">{SIGN_NAMES[item.signCode] ?? item.signCode}</td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {item.issues.map((iss) => (
                            <span
                              key={iss}
                              className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-200 text-xs"
                              title={iss}
                            >
                              {ISSUE_LABELS[iss] ?? iss}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 text-right text-white/40 text-xs">v{item.contentVersion}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data && lowQ === 0 && total > 0 && (
        <div className="card p-6 bg-emerald-500/10 border-emerald-500/30 text-emerald-200">
          <p className="flex items-center gap-2 font-medium">
            <CheckCircle2 size={20} /> Все {total} записей в этом диапазоне — high quality.
          </p>
          <p className="text-sm text-emerald-200/70 mt-1">
            collective_context присутствует, forecast_variants полные, archetype/tone_profile настоящие.
          </p>
        </div>
      )}
    </div>
  );
}

// ----- Helpers -----

function RowStatusCell({ state, hasState }: { state: RowState; hasState: boolean }) {
  if (!hasState) {
    return (
      <span className="inline-flex items-center gap-1.5 text-white/30 text-xs" title="не запущено">
        <span className="w-2 h-2 rounded-full bg-white/20" />
        не в очереди
      </span>
    );
  }
  if (state.kind === "idle") {
    return (
      <span className="inline-flex items-center gap-1.5 text-white/50 text-xs" title="ждёт очереди">
        <Hourglass size={14} className="text-amber-400" />
        ждёт
      </span>
    );
  }
  if (state.kind === "running") {
    const elapsedSec = Math.round((Date.now() - state.startedAt) / 1000);
    return (
      <span className="inline-flex items-center gap-1.5 text-astro-violet text-xs" title="выполняется">
        <Loader2 size={14} className="animate-spin" />
        {elapsedSec}s
      </span>
    );
  }
  if (state.kind === "ok") {
    const sec = (state.durationMs / 1000).toFixed(1);
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs"
        title={`готово за ${sec}s, потрачено ${state.costRub.toFixed(2)} ₽`}>
        <CheckCircle2 size={14} />
        {sec}s · {state.costRub.toFixed(2)}₽
      </span>
    );
  }
  // fail
  return (
    <span className="inline-flex items-center gap-1.5 text-rose-300 text-xs"
      title={state.error}>
      <XCircle size={14} />
      <span className="truncate max-w-[140px]">{state.error}</span>
    </span>
  );
}

function Stat({
  label, value, hint, accent = "neutral",
}: {
  label: string;
  value: number | string;
  hint?: string;
  accent?: "ok" | "warn" | "neutral";
}) {
  const colorCls =
    accent === "ok"
      ? "text-emerald-300"
      : accent === "warn"
      ? "text-amber-300"
      : "text-white";
  return (
    <div className="bg-black/20 rounded-lg px-4 py-3">
      <div className="text-xs text-white/40 mb-1">{label}</div>
      <div className={`text-2xl font-display font-semibold ${colorCls}`}>{value}</div>
      {hint && <div className="text-xs text-white/30 mt-1">{hint}</div>}
    </div>
  );
}

function formatSec(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}
